import styled, { css, RuleSet } from 'styled-components'

import { withBorder } from '@/theme/utils'

import { ButtonSizeKeys, ButtonUseKeys } from './button.types'

export const Button = styled.button<{
  $use: ButtonUseKeys
  $size: ButtonSizeKeys
  $onlyBox?: boolean
  $onlyCircle?: boolean
  $overrideDisabled?: boolean
}>(({ theme, $use, $size, $onlyBox, $onlyCircle, $overrideDisabled }) => {
  const baseStyles = css`
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: fit-content;
    cursor: pointer;
    overflow: initial;
    padding: 0;
    margin: 0;
    border-radius: unset;
    user-select: none;
    border: none;
    outline: none;
    background: none;

    &:focus-visible {
      outline: -webkit-focus-ring-color auto ${theme.border.size['1']};
    }

    &:disabled {
      cursor: not-allowed;
    }

    > [data-button-children-wrapper] > svg {
      aspect-ratio: 1 / 1;
    }
  `

  const hasBorder = $use === 'secondary-gray'

  const sizeStyles: Record<ButtonSizeKeys, RuleSet> = {
    large: css`
      font-size: 1.8rem;
      line-height: 2.4rem;
      font-weight: 500;
      padding: ${withBorder(theme, 12, hasBorder)};

      ${
        !$onlyBox &&
        css`
        padding-inline: ${withBorder(theme, 24, hasBorder)};
      `
      }

      > [data-button-children-wrapper] > svg {
        width: 2.4rem;
      }
    `,
    medium: css`
      font-size: 1.4rem;
      line-height: 2rem;
      font-weight: 500;
      padding: ${withBorder(theme, 12, hasBorder)};

      ${
        !$onlyBox &&
        css`
        padding-inline: ${withBorder(theme, 16, hasBorder)};
      `
      }

      > [data-button-children-wrapper] > svg {
        width: 2rem;
      }
    `,
    small: css`
      font-size: 1.2rem;
      line-height: 1.6rem;
      font-weight: 400;
      padding: ${withBorder(theme, 12, hasBorder)};

      ${
        !$onlyBox &&
        css`
        padding-inline: ${withBorder(theme, 16, hasBorder)};
      `
      }

      > [data-button-children-wrapper] > svg {
        width: 1.6rem;
      }
    `,
    unset: css``,
  }

  const appearanceStyles: Record<ButtonUseKeys, RuleSet> = {
    primary: css`
      color: ${theme.colors.white};
      background: ${theme.colors.marketShopBrand800};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          background: ${theme.colors.marketShopBrand800};
        }

        &:disabled {
          background: ${theme.colors.marketShopBrand400};
        }
      `
      }
    `,
    'primary-light': css`
      color: ${theme.colors.marketShopBrand800};
      background: ${theme.colors.marketShopBrand100};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          color: ${theme.colors.marketShopBrand800};
          background: ${theme.colors.marketShopBrand100};
        }

        &:disabled {
          color: ${theme.colors.marketShopBrand300};
          background: ${theme.colors.marketShopBrand300};
        }
      `
      }
    `,
    'secondary-gray': css`
      color: ${theme.colors.black600};
      background: ${theme.colors.gray100};
      border: ${theme.border.size['1']} solid ${theme.colors.gray600};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          color: ${theme.colors.black600};
          background: ${theme.colors.gray100};
          border: ${theme.border.size['1']} solid ${theme.colors.gray600};
        }

        &:disabled {
          color: ${theme.colors.black200};
          border-color: ${theme.colors.gray600};
          background: ${theme.colors.gray600};
        }
      `
      }
    `,
    'transparent-dangerous': css`
      color: ${theme.colors.red500};
      border-radius: ${theme.border.radius['4']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          color: ${theme.colors.red500};
        }

        &:disabled {
          color: ${theme.colors.red200};
        }
      `
      }
    `,
    unset: css``,
  }

  const circleStyles =
    $onlyCircle &&
    css`
      border-radius: ${theme.border.radius['circle']};
    `

  const loadingStyles =
    $overrideDisabled &&
    css`
      & > *:not([data-button-loader]) {
        visibility: hidden;
      }
    `

  return css`
    ${baseStyles}
    ${sizeStyles[$size]}
    ${appearanceStyles[$use]}
    ${circleStyles}
    ${loadingStyles}
  `
})

export const ChildrenWrapper = styled.div<{
  $size: ButtonSizeKeys
}>(({ theme, $size }) => {
  const baseStyles = css`
    display: flex;
    align-items: center;
  `

  const sizeStyles: Record<ButtonSizeKeys, RuleSet> = {
    large: css`
      gap: ${theme.space['8']};
    `,
    medium: css`
      gap: ${theme.space['4']};
    `,
    small: css`
      gap: ${theme.space['4']};
    `,
    unset: css`
      gap: ${theme.space['4']};
    `,
  }

  return css`
    ${baseStyles}
    ${sizeStyles[$size]}
  `
})
